import { useState } from 'react';
import './HistoryPage.css';
import { Layout } from '../../components/Layout/Layout';
import { useUser } from '../../app/UserContext';
import { MonthNav } from '../../components/MonthNav/MonthNav';
import { getCurrentMonthKey } from '../../domain/workouts/types';
import type { Workout } from '../../domain/workouts/types';
import { deleteWorkout } from '../../infrastructure/repositories/workoutRepository';
import { getPreviousMonthKey, getNextMonthKey } from '../../domain/leaderboard/types';
import { useMonthlyHistory } from './useMonthlyHistory';
import { HistoryView } from './HistoryView';
import { TrendCharts } from './TrendCharts';
import { TrophyCase } from '../awards/TrophyCase';
import { useAwardsData } from '../awards/useAwardsData';
import { buildTrophyCase } from '../../domain/awards/computeAwards';

/**
 * Month is the log; Graph is one chosen lift over time; Trophies is what you
 * have won and what you are close to winning.
 *
 * Trophies took the slot the per-category "Best" chart had. The per-lift Graph
 * covers the same ground one lift at a time, so nothing became unreachable.
 */
type HistoryTab = 'month' | 'graph' | 'trophies';

const TABS: { id: HistoryTab; label: string }[] = [
  { id: 'month', label: 'Month' },
  { id: 'graph', label: 'Graph' },
  { id: 'trophies', label: 'Trophies' },
];

export function HistoryPage() {
  const { user } = useUser();
  const currentMonthKey = getCurrentMonthKey();

  const [activeMonthKey, setActiveMonthKey] = useState(currentMonthKey);
  const [tab, setTab] = useState<HistoryTab>('month');
  const [workoutToDelete, setWorkoutToDelete] = useState<Workout | null>(null);

  const { workouts, summary, previousSummary, isLoading, reload } = useMonthlyHistory(
    user?.id,
    activeMonthKey,
  );

  // Only loaded once the tab is opened: the trophy case needs everyone's lifts
  // to know which social awards you won, which the month view never asks for.
  const { data: awardsData, isLoading: trophiesLoading } = useAwardsData(tab === 'trophies');
  const trophyMonths =
    awardsData && user ? buildTrophyCase(awardsData.workouts, awardsData.users, user.id) : [];

  const handlePreviousMonth = () => {
    setActiveMonthKey(getPreviousMonthKey(activeMonthKey));
  };

  const handleNextMonth = () => {
    if (activeMonthKey !== currentMonthKey) {
      setActiveMonthKey(getNextMonthKey(activeMonthKey));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!workoutToDelete || !user) return;

    try {
      await deleteWorkout(workoutToDelete.id);
      await reload();
    } catch (error) {
      console.error('Failed to delete workout:', error);
    } finally {
      setWorkoutToDelete(null);
    }
  };

  const isCurrentMonth = activeMonthKey === currentMonthKey;

  return (
    <Layout>
      <div className="history-page">
        <div className="history-page__tabs" role="group" aria-label="History view">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              className={`history-page__tab ${tab === id ? 'history-page__tab--active' : ''}`}
              onClick={() => setTab(id)}
              aria-pressed={tab === id}
            >
              {label}
            </button>
          ))}
        </div>

        {/* The charts span a trailing six months of their own, so the month
            arrows would imply an effect they do not have. */}
        {tab === 'month' && (
          <MonthNav
            monthKey={activeMonthKey}
            onPrevious={handlePreviousMonth}
            onNext={handleNextMonth}
            isNextDisabled={isCurrentMonth}
          />
        )}

        <div className="history-page__content">
          {tab === 'month' ? (
            <HistoryView
              workouts={workouts}
              summary={summary}
              previousSummary={previousSummary}
              isLoading={isLoading}
              isCurrentMonth={isCurrentMonth}
              onDeleteWorkout={setWorkoutToDelete}
              emptyHint="Go to Log to record your first workout!"
            />
          ) : tab === 'trophies' ? (
            <TrophyCase months={trophyMonths} isLoading={trophiesLoading} />
          ) : (
            <TrendCharts userId={user?.id} view="exercise" />
          )}
        </div>

        {workoutToDelete && (
          <div className="history-modal">
            <div className="history-modal__content">
              <h3>Delete Workout</h3>
              <p>Are you sure you want to delete this workout?</p>
              <div className="history-modal__actions">
                <button
                  className="history-modal__button history-modal__button--cancel"
                  onClick={() => setWorkoutToDelete(null)}
                >
                  Cancel
                </button>
                <button
                  className="history-modal__button history-modal__button--delete"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
